import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Alert,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { providersAPI, pricingAPI, vmTypesAPI, regionsAPI, databaseEditionsAPI } from '../../../services/api';
import { Provider, VMType, Region, DatabaseEdition, DatabaseEditionPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { monthlyCost?: number; isAvailable?: boolean };
}

interface PriceInputProps {
  pricingId: number;
  value: number;
  onChange: (pricingId: number, field: string, value: number) => void;
}

// Memoized price input component
const PriceInput = React.memo(({ pricingId, value, onChange }: PriceInputProps) => (
  <TextField
    type="number"
    size="small"
    value={value}
    onChange={(e) => {
      const newValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
      onChange(pricingId, 'monthlyCost', newValue);
    }}
    inputProps={{ step: 0.01, min: 0 }}
    sx={{
      width: 100,
      '& .MuiInputBase-input': {
        fontSize: '0.875rem',
        py: 0.5,
        px: 1,
      },
    }}
  />
));

interface AvailabilityCheckboxProps {
  pricingId: number;
  checked: boolean;
  onChange: (pricingId: number, field: string, value: boolean) => void;
}

// Memoized checkbox component
const AvailabilityCheckbox = React.memo(({ pricingId, checked, onChange }: AvailabilityCheckboxProps) => (
  <Checkbox
    checked={checked}
    onChange={(e) => onChange(pricingId, 'isAvailable', e.target.checked)}
  />
));

interface VMSection {
  vm: VMType;
  edition: DatabaseEdition;
  vmPricing: DatabaseEditionPricingRow[];
}

const DatabaseVMPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [vmTypes, setVMTypes] = useState<VMType[]>([]);
  const [databaseEditions, setDatabaseEditions] = useState<DatabaseEdition[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<DatabaseEditionPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<PriceChangeMap>({});
  const [recalculating, setRecalculating] = useState(false);
  const [renderedCount, setRenderedCount] = useState(100);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Memoize pricing lookup by VM
  const pricingByVM = useMemo(() => {
    const grouped: Record<number, DatabaseEditionPricingRow[]> = {};
    pricing.forEach(p => {
      if (!grouped[p.VMTypeId]) {
        grouped[p.VMTypeId] = [];
      }
      grouped[p.VMTypeId]!.push(p);
    });
    return grouped;
  }, [pricing]);

  // Create flattened list of VM + Edition combinations for progressive rendering
  const vmSections = useMemo(() => {
    const sections: VMSection[] = [];
    vmTypes.forEach(vm => {
      const vmPricingEntries = pricingByVM[vm.VMTypeId] || [];
      if (vmPricingEntries.length === 0) return;

      const uniqueEditions = [...new Set(vmPricingEntries.map(p => p.DatabaseEditionId))].sort();
      if (uniqueEditions.length === 0) return;

      uniqueEditions.forEach(editionId => {
        const edition = databaseEditions.find(e => e.DatabaseEditionId === editionId);
        if (edition) {
          sections.push({ vm, edition, vmPricing: vmPricingEntries });
        }
      });
    });
    return sections;
  }, [vmTypes, pricingByVM, databaseEditions]);

  // Progressive loading effect
  useEffect(() => {
    if (vmSections.length === 0 || renderedCount >= vmSections.length) return;

    setIsLoadingMore(true);
    const timer = setTimeout(() => {
      setRenderedCount(prev => Math.min(prev + 50, vmSections.length));
      setIsLoadingMore(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [vmSections.length, renderedCount]);

  // Reset rendered count when provider changes
  useEffect(() => {
    setRenderedCount(100);
  }, [selectedProvider]);

  const fetchInitialData = async () => {
    try {
      const [providersRes, vmTypesRes, editionsRes] = await Promise.all([
        providersAPI.getAll(),
        vmTypesAPI.getAll(),
        databaseEditionsAPI.getAll(),
      ]);
      setProviders(providersRes.data.filter((p: Provider) => p.IsActive));
      setVMTypes(vmTypesRes.data.filter((v: VMType) => v.IsActive));
      setDatabaseEditions(editionsRes.data.filter((e: DatabaseEdition) => e.IsActive));
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;

    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getDatabaseVMPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);

      setPricing(pricingRes.data);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setChanges({});
      setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(`Failed to load pricing: ${axiosErr.response?.data?.error || axiosErr.message}`);
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (providers.length > 0) {
      fetchPricingData();
    }
  }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number, field: string, value: number | boolean) => {
    setChanges({
      ...changes,
      [pricingId]: {
        ...changes[pricingId],
        [field]: value,
      },
    });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const originalPricing = pricing.find(p => p.DatabaseEditionPricingId === parseInt(pricingId));
        const mergedData = {
          monthlyCost: changedFields.monthlyCost !== undefined
            ? changedFields.monthlyCost
            : originalPricing?.MonthlyCost ?? 0,
          isAvailable: changedFields.isAvailable !== undefined
            ? changedFields.isAvailable
            : originalPricing?.IsAvailable ?? 0,
        };
        return pricingAPI.updateDatabaseVMPrice(pricingId, mergedData);
      });

      await Promise.all(updates);
      setSuccess('Prices updated successfully');
      setChanges({});
      fetchPricingData();

      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to update prices');
    }
  };

  const handleRecalculate = async () => {
    const currentProvider = providers[selectedProvider];
    if (!currentProvider) return;

    setRecalculating(true);
    setError('');
    setSuccess('');

    try {
      const res = await pricingAPI.recalculateDatabaseVMPricing(currentProvider.ProviderId);
      setSuccess(res.data.message);
      setChanges({});
      fetchPricingData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(axiosErr.response?.data?.error || 'Failed to recalculate prices');
    } finally {
      setRecalculating(false);
    }
  };

  const getPriceValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.monthlyCost !== undefined
      ? changes[pricingId]!.monthlyCost!
      : defaultValue;
  };

  const getAvailableValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.isAvailable !== undefined
      ? changes[pricingId]!.isAvailable!
      : !!defaultValue;
  };

  const handleToggleAll = (entries: DatabaseEditionPricingRow[], value: boolean) => {
    const newChanges: PriceChangeMap = { ...changes };
    entries.forEach(entry => {
      if (entry) {
        newChanges[entry.DatabaseEditionPricingId] = {
          ...newChanges[entry.DatabaseEditionPricingId],
          isAvailable: value,
        };
      }
    });
    setChanges(newChanges);
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">SQL Server on VM Pricing</Typography>
          {isLoadingMore && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={12} />
              Loading more... ({renderedCount} of {vmSections.length})
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={recalculating ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRecalculate}
            disabled={recalculating || Object.keys(changes).length > 0}
          >
            {recalculating ? 'Recalculating...' : 'Recalculate from VM Prices'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
            disabled={Object.keys(changes).length === 0}
          >
            Save Changes ({Object.keys(changes).length})
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> These prices are the FULL BASE PRICE for the VM + OS + SQL Edition combined.
          The total cost in the quote builder will be: Base Price + Disk Prices.
        </Typography>
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={selectedProvider}
          onChange={(_e, newValue: number) => setSelectedProvider(newValue)}
          variant="fullWidth"
        >
          {providers.map((provider) => (
            <Tab key={provider.ProviderId} label={provider.ProviderName} />
          ))}
        </Tabs>
      </Paper>

      {vmSections.slice(0, renderedCount).map(({ vm, edition, vmPricing: vmPricingEntries }) => {
        const editionPricing = vmPricingEntries.filter(p => p.DatabaseEditionId === edition.DatabaseEditionId);

        return (
          <Paper key={`${vm.VMTypeId}-${edition.DatabaseEditionId}`} sx={{ mb: 3, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  {vm.VMName} - {vm.CPU} vCPU, {vm.RAM}GB RAM
                </Typography>
                <Chip
                  label={edition.EditionName}
                  size="small"
                  color="primary"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button size="small" variant="outlined" color="success" onClick={() => handleToggleAll(editionPricing, true)}>Enable All</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => handleToggleAll(editionPricing, false)}>Disable All</Button>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    {regions.map((region) => (
                      <TableCell key={region.RegionId} align="center">
                        {region.RegionName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Monthly Cost ($)</TableCell>
                    {regions.map((region) => {
                      const priceEntry = editionPricing.find(p => p.RegionId === region.RegionId);
                      if (!priceEntry) {
                        return (
                          <TableCell key={region.RegionId} align="center">
                            <Typography variant="caption" color="text.secondary">
                              Not configured
                            </Typography>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={region.RegionId} align="center">
                          <PriceInput
                            pricingId={priceEntry.DatabaseEditionPricingId}
                            value={getPriceValue(priceEntry.DatabaseEditionPricingId, priceEntry.MonthlyCost)}
                            onChange={handlePriceChange}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    {regions.map((region) => {
                      const priceEntry = editionPricing.find(p => p.RegionId === region.RegionId);
                      if (!priceEntry) return <TableCell key={region.RegionId} />;

                      return (
                        <TableCell key={region.RegionId} align="center">
                          <AvailabilityCheckbox
                            pricingId={priceEntry.DatabaseEditionPricingId}
                            checked={getAvailableValue(priceEntry.DatabaseEditionPricingId, priceEntry.IsAvailable)}
                            onChange={handlePriceChange}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}

      {isLoadingMore && renderedCount < vmSections.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default DatabaseVMPricing;
